import {
  Children,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import styles from './HorizontalCarousel.module.css';

export const CAROUSEL_ITEM_SELECTOR = '[data-carousel-item="true"]';

type HorizontalCarouselProps = {
  ariaLabel: string;
  prevAriaLabel: string;
  nextAriaLabel: string;
  children: ReactNode;
  /** Re-check arrow disabled state / reset position when scroll content changes. */
  contentKey?: string | number;
  /** Optional class on the horizontal scroll track (e.g. force LTR item order). */
  scrollClassName?: string;
  /** Optional class on each scroll snap item wrapper. */
  trackItemClassName?: string;
  /** Leading/trailing spacers so slides center with neighbors on both sides (needs ≥3 items). */
  centerPeek?: boolean;
  /** Initial horizontal scroll position when content loads. */
  initialScrollAlign?: 'start' | 'end';
  /** Swap prev/next arrow actions when the track scrolls RTL (Hebrew asset rows). */
  rtlNav?: boolean;
  /** Hebrew row: children reversed; first slide flush right; scroll left for more. */
  rtlEdgePeek?: boolean;
};

function listCarouselTrackItems(scrollEl: HTMLElement): HTMLElement[] {
  return Array.from(scrollEl.children).filter(
    (node): node is HTMLElement =>
      node instanceof HTMLElement && node.matches(CAROUSEL_ITEM_SELECTOR),
  );
}

function trackGapPx(scrollEl: HTMLElement): number {
  const gap = Number.parseFloat(
    getComputedStyle(scrollEl).columnGap || getComputedStyle(scrollEl).gap || '0',
  );
  return Number.isFinite(gap) ? gap : 0;
}

function maxScrollLeft(scrollEl: HTMLElement): number {
  return Math.max(0, scrollEl.scrollWidth - scrollEl.clientWidth);
}

function normalizedScrollLeft(scrollEl: HTMLElement): number {
  const rtl = getComputedStyle(scrollEl).direction === 'rtl';
  return rtl ? -scrollEl.scrollLeft : scrollEl.scrollLeft;
}

/** Center a track item horizontally without scrolling ancestor page panels. */
function scrollTrackItemInlineCenter(
  scrollEl: HTMLElement,
  item: HTMLElement,
  behavior: ScrollBehavior = 'auto',
): void {
  const rtl = getComputedStyle(scrollEl).direction === 'rtl';
  const maxScroll = maxScrollLeft(scrollEl);
  const desired = item.offsetLeft - (scrollEl.clientWidth - item.offsetWidth) / 2;
  const clamped = Math.max(0, Math.min(desired, maxScroll));
  if (typeof scrollEl.scrollTo === 'function') {
    scrollEl.scrollTo({ left: rtl ? -clamped : clamped, behavior });
  } else {
    scrollEl.scrollLeft = rtl ? -clamped : clamped;
  }
}

/** Hebrew peek: children are reversed; home = scrolled all the way right. */
function rtlEdgePeekGoHome(scrollEl: HTMLElement, behavior: ScrollBehavior = 'auto'): void {
  const target = maxScrollLeft(scrollEl);
  if (typeof scrollEl.scrollTo === 'function') {
    scrollEl.scrollTo({ left: target, behavior });
  } else {
    scrollEl.scrollLeft = target;
  }
}

function rtlEdgePeekStepSize(scrollEl: HTMLElement, items: HTMLElement[]): number {
  const gap = trackGapPx(scrollEl);
  const first = items[0];
  if (first && first.offsetWidth > 0) {
    return first.offsetWidth + gap;
  }
  return Math.max(scrollEl.clientWidth * 0.73, 1);
}

function rtlEdgePeekStep(
  scrollEl: HTMLElement,
  direction: -1 | 1,
  behavior: ScrollBehavior = 'smooth',
): void {
  const items = listCarouselTrackItems(scrollEl);
  const step = rtlEdgePeekStepSize(scrollEl, items);
  const maxScroll = maxScrollLeft(scrollEl);
  const next = Math.max(0, Math.min(scrollEl.scrollLeft + direction * step, maxScroll));
  if (typeof scrollEl.scrollTo === 'function') {
    scrollEl.scrollTo({ left: next, behavior });
  } else {
    scrollEl.scrollLeft = next;
  }
}

export function HorizontalCarousel({
  ariaLabel,
  prevAriaLabel,
  nextAriaLabel,
  children,
  contentKey,
  scrollClassName,
  trackItemClassName,
  centerPeek = false,
  initialScrollAlign = 'start',
  rtlNav = false,
  rtlEdgePeek = false,
}: HorizontalCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);
  const [rtlHome, setRtlHome] = useState(true);
  const [rtlTail, setRtlTail] = useState(true);
  const [canScroll, setCanScroll] = useState(false);

  const updateEdges = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const maxScroll = maxScrollLeft(el);
    const scrollable = maxScroll > 1;
    setCanScroll(scrollable);
    if (!scrollable) {
      setAtStart(true);
      setAtEnd(true);
      setRtlHome(true);
      setRtlTail(true);
      return;
    }
    const pos = normalizedScrollLeft(el);
    setAtStart(pos <= 1);
    setAtEnd(pos >= maxScroll - 1);
    setRtlHome(pos >= maxScroll - 1);
    setRtlTail(pos <= 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const onScroll = () => updateEdges();
    el.addEventListener('scroll', onScroll, { passive: true });
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => updateEdges()) : null;
    ro?.observe(el);
    updateEdges();
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro?.disconnect();
    };
  }, [contentKey, updateEdges]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const align = () => {
      if (centerPeek) {
        const items = listCarouselTrackItems(el);
        if (items[0]) {
          scrollTrackItemInlineCenter(el, items[0]);
        }
      } else if (rtlEdgePeek) {
        rtlEdgePeekGoHome(el);
      } else {
        const rtl = getComputedStyle(el).direction === 'rtl';
        const maxScroll = maxScrollLeft(el);
        const target = initialScrollAlign === 'end' ? maxScroll : 0;
        if (typeof el.scrollTo === 'function') {
          el.scrollTo({ left: rtl ? -target : target, behavior: 'auto' });
        } else {
          el.scrollLeft = rtl ? -target : target;
        }
      }
      updateEdges();
    };
    align();
    requestAnimationFrame(align);
    const t = window.setTimeout(align, 120);
    return () => window.clearTimeout(t);
  }, [contentKey, centerPeek, rtlEdgePeek, initialScrollAlign, updateEdges]);

  const scrollByPage = useCallback((direction: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    if (rtlEdgePeek) {
      rtlEdgePeekStep(el, direction);
      window.setTimeout(updateEdges, 280);
      return;
    }
    if (centerPeek) {
      const items = listCarouselTrackItems(el);
      if (items.length === 0) {
        return;
      }
      const pos = normalizedScrollLeft(el);
      const viewportCenter = pos + el.clientWidth / 2;
      let activeIdx = 0;
      let bestDist = Number.POSITIVE_INFINITY;
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        const itemCenter = item.offsetLeft + item.offsetWidth / 2;
        const dist = Math.abs(itemCenter - viewportCenter);
        if (dist < bestDist) {
          bestDist = dist;
          activeIdx = i;
        }
      }
      const nextIdx = Math.min(items.length - 1, Math.max(0, activeIdx + direction));
      scrollTrackItemInlineCenter(el, items[nextIdx], 'smooth');
      return;
    }
    const rtl = getComputedStyle(el).direction === 'rtl';
    const sign = rtl ? -1 : 1;
    const page = Math.max(el.clientWidth * 0.85, 1);
    const left = direction * sign * page;
    if (typeof el.scrollBy === 'function') {
      el.scrollBy({ left, behavior: 'smooth' });
    } else {
      el.scrollLeft += left;
    }
  }, [centerPeek, rtlEdgePeek, updateEdges]);

  const goStandardPrev = rtlNav ? () => scrollByPage(1) : () => scrollByPage(-1);
  const goStandardNext = rtlNav ? () => scrollByPage(-1) : () => scrollByPage(1);
  const standardPrevDisabled = !canScroll || (rtlNav ? atEnd : atStart);
  const standardNextDisabled = !canScroll || (rtlNav ? atStart : atEnd);

  // Hebrew: left arrow scrolls forward (toward lower scrollLeft); right arrow returns home.
  const leftOnClick = rtlEdgePeek ? () => scrollByPage(-1) : goStandardPrev;
  const rightOnClick = rtlEdgePeek ? () => scrollByPage(1) : goStandardNext;
  const leftDisabled = rtlEdgePeek ? !canScroll || rtlTail : standardPrevDisabled;
  const rightDisabled = rtlEdgePeek ? !canScroll || rtlHome : standardNextDisabled;
  const leftAriaLabel = rtlEdgePeek ? nextAriaLabel : prevAriaLabel;
  const rightAriaLabel = rtlEdgePeek ? prevAriaLabel : nextAriaLabel;

  return (
    <section className={styles.strip} aria-label={ariaLabel}>
      <div className={styles.carousel}>
        <button
          type="button"
          className={styles.navButton}
          aria-label={leftAriaLabel}
          disabled={leftDisabled}
          onClick={leftOnClick}
        >
          <CarouselChevron direction="left" />
        </button>
        <div
          ref={scrollRef}
          className={scrollClassName ? `${styles.scroll} ${scrollClassName}` : styles.scroll}
        >
          {centerPeek ? <div className={styles.peekCenterSpacer} aria-hidden /> : null}
          {Children.toArray(children).map((child, itemIndex) => (
            <div
              key={`item-${itemIndex}`}
              className={[styles.trackItem, trackItemClassName].filter(Boolean).join(' ')}
              data-carousel-item="true"
            >
              {child}
            </div>
          ))}
          {centerPeek ? <div className={styles.peekCenterSpacer} aria-hidden /> : null}
        </div>
        <button
          type="button"
          className={styles.navButton}
          aria-label={rightAriaLabel}
          disabled={rightDisabled}
          onClick={rightOnClick}
        >
          <CarouselChevron direction="right" />
        </button>
      </div>
    </section>
  );
}

function CarouselChevron({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      className={styles.navChevron}
      viewBox="0 0 10 18"
      fill="none"
      aria-hidden
    >
      {direction === 'left' ? (
        <path
          d="M8.5 1.5 2 9l6.5 7.5"
          stroke="currentColor"
          strokeWidth="1.35"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <path
          d="M1.5 1.5 8 9l-6.5 7.5"
          stroke="currentColor"
          strokeWidth="1.35"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}
